/**
 * setInterval that keeps its pace in a hidden tab.
 *
 * Chrome throttles timers on a page that has been hidden for a few minutes to at most one run a minute
 * ("intensive throttling"). The softphone heartbeat runs every 15s and the server pauses the campaign
 * after 60s without one, so a throttled heartbeat that drifts a few seconds past the minute paused live
 * agents whose tab was merely in the background. Timers inside a dedicated worker are not throttled
 * that way, so the worker keeps the beat and posts a message per tick; the callback still runs on the
 * page.
 *
 * Falls back to a plain setInterval wherever a worker can't be had: no Worker (jsdom, old browsers),
 * no blob URLs, or a Content-Security-Policy that blocks blob workers (the worker then fails to load
 * and fires `error`).
 */

const WORKER_SOURCE = `
let intervalId = null

self.onmessage = event => {
  clearInterval(intervalId)
  intervalId = event.data > 0 ? setInterval(() => self.postMessage(0), event.data) : null
}
`

let workerUrl = null

function _createWorker() {
  if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return null
  }

  try {
    // One blob for every interval: its URL is never revoked, so it costs one small string per page
    workerUrl = workerUrl || URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }))

    return new Worker(workerUrl)
  } catch (error) {
    return null
  }
}

/**
 * @returns {{ clear: () => void }} stops the interval; safe to call more than once
 */
export function setBackgroundInterval(callback, intervalInMs) {
  let worker = _createWorker()
  let fallbackIntervalId = null

  const startFallback = () => {
    if (fallbackIntervalId === null) {
      fallbackIntervalId = setInterval(callback, intervalInMs)
    }
  }

  if (worker) {
    worker.onmessage = () => callback()

    worker.onerror = () => {
      worker.terminate()
      worker = null
      startFallback()
    }

    worker.postMessage(intervalInMs)
  } else {
    startFallback()
  }

  return {
    clear() {
      if (worker) {
        worker.terminate()
        worker = null
      }

      if (fallbackIntervalId !== null) {
        clearInterval(fallbackIntervalId)
        fallbackIntervalId = null
      }
    }
  }
}
